import * as icons from '@mui/icons-material';
import { Box, Collapse, IconButton, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TooltipIconButtonLink } from 'components/common/icon/tooltip-icon-button';
import moment from 'moment-timezone';
import { FC, ReactNode, useEffect, useState } from 'react';
import {
  Conversation_externalLead_conversation_inboundCall,
  Conversation_externalLead_conversation_inboundSms,
  Conversation_externalLead_conversation_inboundSms_media,
  Conversation_externalLead_conversation_leadEvent,
  Conversation_externalLead_conversation_leadEvent_events_linkClickedDetails,
  Conversation_externalLead_conversation_outboundCall,
  Conversation_externalLead_conversation_recording,
  Conversation_externalLead_scheduledOutreach,
} from 'types/apollo/Conversation';
import { formatDuration } from 'utils/duration';
import AktifyChatIcon from './aktify-chat-icon.svg';
import { extractExpiration, labelForItem } from './conversation-helpers';
import { DATADOG_MENU_BUTTON_CLASS } from './datadog-message-menu';
import { LeadStatusChip } from './lead-status-chip';

// ============================================================================
// Styled Components
// ============================================================================

export const ChatMessage = styled(Stack)`
  display: flex;
  min-width: 60%;
  max-width: 80%;

  &.left {
    align-self: flex-start;
    align-items: flex-start;
  }

  &.right {
    align-self: flex-end;
    align-items: flex-end;
  }
`;

export const ChatBubble = styled('div')`
  background-color: #318adc;
  color: #fff;
  border-radius: 5px;
  padding: 10px;
  align-self: flex-start;
  margin-left: 8px;
  margin-right: 8px;

  &.right {
    background-color: white;
    color: #2e2e2e;
    align-self: flex-end;
  }

  &.call {
    background-color: #2e2e2e1a;
    color: #2e2e2e;
  }
`;

export const Timestamp = styled('span')`
  font-size: 0.8rem;
  color: #888;
  margin-top: 5px;
  text-align: right;
`;

const CallStatusIcon = styled(Box)(() => ({
  borderRadius: '50%',
  height: 30,
  width: 30,
  backgroundColor: '#2E2E2E',
  color: 'white',
  textAlign: 'center',
  fontSize: 15,
  padding: '2px 0px',
  '&.error': {
    backgroundColor: '#cb6363',
  },
}));

export const AktifyIcon2 = styled(Box)(() => ({
  height: 30,
  content: `url(${AktifyChatIcon})`,
}));

export const LeadChatCircle = styled(Box)(() => ({
  backgroundColor: 'white',
  borderRadius: '50%',
  width: 30,
  height: 30,
  color: '#2E2E2E',
  padding: 4,
  fontWeight: 'bold',
  fontSize: 15,
  textAlign: 'center',
}));

// ============================================================================
// Helper Functions
// ============================================================================

export const iconForItem = (it: Conversation_externalLead_scheduledOutreach): ReactNode => {
  switch (it.kind) {
    case 'REM':
      return <icons.Alarm />;
    case 'SCHEDULE':
      return <icons.Call />;
    case 'FLOW': {
      const intent = it.intent ?? (it.messagePreview ? 'sms' : undefined);
      if (intent === 'call') return <icons.Call />;
      if (intent === 'sms') return <icons.Sms />;
      return <icons.AutoAwesomeMotion />;
    }
    case 'REPLY':
      return <icons.Reply />;
    default:
      return <icons.Schedule />;
  }
};

// ============================================================================
// Components
// ============================================================================

export const OutboundCallStatus: FC<{
  callEvent: Conversation_externalLead_conversation_outboundCall;
}> = ({ callEvent }) => {
  let icon = <icons.Clear />;
  let className = '';
  let text = '';

  if (callEvent.isDnc) {
    className = 'error';
    text = 'DNC (pressed 9)';
  } else if (callEvent.isTransfer) {
    icon = <icons.NorthWest />;
    text = 'transferred (pressed 1)';
  } else if (callEvent.isReschedule) {
    icon = <icons.NorthWest />;
    text = 'rescheduled (pressed 2)';
  } else {
    className = 'error';
    text = 'no response';
  }

  return (
    <>
      <ChatBubble className="call">
        Outbound call <>&mdash;</> {text}
      </ChatBubble>
      <CallStatusIcon className={className}>{icon}</CallStatusIcon>
    </>
  );
};

export const InboundCallStatus: FC<{
  callEvent: Conversation_externalLead_conversation_inboundCall;
}> = ({ callEvent }) => {
  let icon = <icons.Clear />;
  let className = '';
  let text = '';

  if (callEvent.isDnc) {
    className = 'error';
    text = 'DNC (pressed 9)';
  } else if (callEvent.isTransfer) {
    icon = <icons.NorthEast />;
    text = 'transferred (pressed 1)';
  } else if (callEvent.noResponse) {
    className = 'error';
    text = 'no response';
  }

  return (
    <>
      <CallStatusIcon className={className}>{icon}</CallStatusIcon>
      <ChatBubble className="call">
        Inbound call <>&mdash;</> {text}
      </ChatBubble>
    </>
  );
};

export const Recording: FC<{
  recording: Conversation_externalLead_conversation_recording;
}> = ({ recording }) => {
  const expires = extractExpiration(recording.recordingUrl);
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    if (expires == null) {
      return;
    }

    const intervalId = setInterval(() => {
      setCurrentTime(Date.now());
    }, 30000);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <ChatBubble className="call" sx={{ width: '100%' }}>
      <Stack>
        <Typography>
          {recording.recordingUrl ? 'Recording' : 'Recording not available'} <>&mdash;</>{' '}
          {recording.isTransfer ? 'qualified transfer' : 'non-qualified transfer'}
        </Typography>
        {recording.recordingUrl ? (
          <audio controls src={recording.recordingUrl} style={{ width: '100%', minWidth: 300 }} />
        ) : (
          <Typography>Duration: {formatDuration(recording.duration)}</Typography>
        )}

        {expires != null && expires < currentTime && (
          <Typography color="#ff3f14">
            For security purposes, access to this recording has expired. To play the recording,
            please refresh the page.
          </Typography>
        )}
      </Stack>
    </ChatBubble>
  );
};

export const LeadEventCard: FC<{
  leadEvent: Conversation_externalLead_conversation_leadEvent;
  leadTimeZone: string;
}> = ({ leadEvent, leadTimeZone }) => {
  const firstAppointmentDetails = leadEvent.events?.[0]?.appointmentDetails;

  if (!firstAppointmentDetails) {
    return null;
  }

  let displayStatus = firstAppointmentDetails.appointmentStatus?.toLowerCase() || '';
  let statusColor = '#CBCED2';
  let statusIcon = <icons.Event />;

  if (leadEvent.cancelledTime) {
    displayStatus = 'cancelled';
  }

  switch (displayStatus) {
    case 'confirmed':
      statusColor = '#91E0C4';
      statusIcon = <icons.EventAvailable />;
      break;
    case 'cancelled':
      statusColor = '#E99987';
      statusIcon = <icons.EventBusy />;
      break;
    case 'rescheduled':
      statusColor = '#F3DA99';
      statusIcon = <icons.Update />;
      break;
    default:
      statusColor = '#c7b5f3';
      break;
  }

  const appointmentTime = leadEvent.rescheduledTime ?? leadEvent.eventTime;

  const timeChanges: {
    time: string;
    type: 'original' | 'rescheduled_to';
  }[] = [];

  leadEvent.events?.forEach((event) => {
    if (
      !event?.appointmentDetails?.previousAppointmentStartTime &&
      event?.appointmentDetails?.appointmentStartTime
    ) {
      timeChanges.push({
        time: event.appointmentDetails.appointmentStartTime,
        type: 'original',
      });
    }
    if (
      event?.appointmentDetails?.previousAppointmentStartTime &&
      event?.appointmentDetails?.appointmentStartTime &&
      new Date(event.appointmentDetails.previousAppointmentStartTime).getTime() !=
        new Date(event.appointmentDetails.appointmentStartTime).getTime()
    ) {
      timeChanges.push({
        time: event.appointmentDetails.appointmentStartTime,
        type: 'rescheduled_to',
      });
    }
  });

  timeChanges.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

  const formattedTime = appointmentTime
    ? moment(appointmentTime).tz(leadTimeZone).format('dddd MMM D, YYYY [at] h:mm A z')
    : 'Time not specified';

  const StatusIconBox = styled(Box)(() => ({
    borderRadius: '50%',
    height: 30,
    width: 30,
    backgroundColor: statusColor,
    color: 'white',
    textAlign: 'center',
    fontSize: 15,
    padding: '2px 0px',
  }));

  return (
    <ChatBubble className="call" sx={{ width: '100%' }}>
      <Stack spacing={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <StatusIconBox>{statusIcon}</StatusIconBox>
          <Typography fontWeight="medium">
            {firstAppointmentDetails.title || 'Appointment'}
          </Typography>
          {displayStatus && <LeadStatusChip status={displayStatus} />}
        </Box>

        <Stack spacing={0.5}>
          <Typography>
            <icons.Schedule fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
            {formattedTime}
          </Typography>
          {firstAppointmentDetails.contactId && firstAppointmentDetails.locationId && (
            <Typography>
              <icons.AccountBox fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
              <a
                href={`https://app.gohighlevel.com/v2/location/${firstAppointmentDetails.locationId}/contacts/detail/${firstAppointmentDetails.contactId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#318adc', textDecoration: 'none' }}
              >
                Contact Details{' '}
                <icons.OpenInNewOutlined
                  color="action"
                  fontSize="small"
                  sx={{ verticalAlign: 'middle' }}
                />
              </a>
            </Typography>
          )}

          {firstAppointmentDetails.contactId && !firstAppointmentDetails.locationId && (
            <Typography>
              <icons.AccountBox fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
              Contact ID: {firstAppointmentDetails.contactId}
            </Typography>
          )}

          {/*{firstAppointmentDetails.address && (*/}
          {/*  <Typography>*/}
          {/*    <icons.LocationOn fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />*/}
          {/*    {mapsLink ? (*/}
          {/*      <a*/}
          {/*        href={mapsLink}*/}
          {/*        target="_blank"*/}
          {/*        rel="noopener noreferrer"*/}
          {/*        style={{ color: '#318adc', textDecoration: 'none' }}*/}
          {/*      >*/}
          {/*        {firstAppointmentDetails.address}*/}
          {/*      </a>*/}
          {/*    ) : (*/}
          {/*      firstAppointmentDetails.address*/}
          {/*    )}*/}
          {/*  </Typography>*/}
          {/*)}*/}

          {timeChanges.length > 1 && (
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
              <Typography variant="subtitle2" fontWeight="medium" sx={{ mb: 0.5 }}>
                Appointment Timeline:
              </Typography>

              {timeChanges.map((change, index) => {
                let icon: ReactNode, label: string, textColor: string;

                switch (change.type) {
                  case 'original':
                    icon = <icons.Flag fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />;
                    label = 'Initially scheduled for';
                    textColor = 'text.secondary';
                    break;
                  case 'rescheduled_to':
                    icon = (
                      <icons.Update fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    );
                    label = 'Changed to';
                    textColor = '#684b00';
                    break;
                  default:
                    icon = <icons.Event fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />;
                    label = 'Scheduled for';
                    textColor = 'text.primary';
                    break;
                }

                return (
                  <Typography key={index} variant="body2" sx={{ color: textColor, ml: 1 }}>
                    {icon}
                    {label}:{' '}
                    {moment(change.time).tz(leadTimeZone).format('MMM D, YYYY [at] h:mm A z')}
                  </Typography>
                );
              })}
            </Box>
          )}

          {leadEvent.cancelledTime && (
            <Typography sx={{ mt: 0.5, color: '#E99987', fontWeight: 'medium' }}>
              <icons.Cancel fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
              Cancelled at {moment(leadEvent.cancelledTime).format('MMM D, YYYY [at] h:mm A')}
            </Typography>
          )}
        </Stack>
      </Stack>
    </ChatBubble>
  );
};

// Unnamed links fall back to the destination, minus the protocol/www noise.
const displayUrl = (url: string): string => url.replace(/^https?:\/\/(www\.)?/, '');

const LinkDetailRow: FC<{ label: string; value?: string | null }> = ({ label, value }) => {
  if (!value) {
    return null;
  }
  return (
    <Stack spacing="2px">
      <Typography component="span" sx={{ fontSize: 12, color: '#9CA3AF' }}>
        {label}
      </Typography>
      <Typography component="span" sx={{ fontSize: 12, color: '#525252' }}>
        {value}
      </Typography>
    </Stack>
  );
};

export const LinkClickEventCard: FC<{
  linkClickedDetails: Conversation_externalLead_conversation_leadEvent_events_linkClickedDetails;
  leadTimeZone: string;
}> = ({ linkClickedDetails, leadTimeZone }) => {
  const [expanded, setExpanded] = useState(false);
  const { linkKey, url, shortUrl, ip, ipClickedCount, clickedAt } = linkClickedDetails;
  const suspiciousBotClick = ipClickedCount > 1;
  const otherClicks = ipClickedCount - 1;

  const formattedTime = clickedAt
    ? moment(clickedAt).tz(leadTimeZone).format('h:mm A z')
    : undefined;
  const eyebrow = [suspiciousBotClick ? 'Possible bot' : 'Link clicked', formattedTime]
    .filter(Boolean)
    .join(' · ');
  const title = linkKey || (url ? displayUrl(url) : 'Link');
  const hasDetails = !!(url || shortUrl || ip);

  return (
    <Box
      sx={{
        ml: 1,
        width: 'min(600px, 80%)',
        maxWidth: '100%',
        backgroundColor: '#fff',
        border: '1px solid #E5E7EB',
        borderRadius: '12px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        overflow: 'hidden',
      }}
    >
      <Box display="flex" alignItems="center" gap="10px" sx={{ p: '12px 10px 12px 14px' }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: suspiciousBotClick ? '#FEF3C7' : '#FEE2E2',
            color: suspiciousBotClick ? '#B45309' : '#DD2424',
          }}
        >
          {suspiciousBotClick ? (
            <icons.SmartToyOutlined sx={{ fontSize: 16 }} />
          ) : (
            <icons.Link sx={{ fontSize: 16 }} />
          )}
        </Box>
        {/* `contain: inline-size` keeps long names/URLs from widening the card (and the
            content-sized chat column around it), so the width doesn't jump on expand. */}
        <Stack flex={1} minWidth={0} sx={{ contain: 'inline-size' }}>
          <Typography
            component="span"
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: suspiciousBotClick ? '#B45309' : '#9CA3AF',
            }}
          >
            {eyebrow}
          </Typography>
          <Typography component="span" noWrap title={title} sx={{ fontSize: 15, fontWeight: 700 }}>
            {title}
          </Typography>
        </Stack>
        {hasDetails && (
          <IconButton
            size="small"
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
            aria-label={expanded ? 'Hide link details' : 'Show link details'}
            title={expanded ? 'Hide link details' : 'Show link details'}
            sx={{ borderRadius: '8px', color: '#525252', flexShrink: 0 }}
          >
            <icons.ExpandMore
              fontSize="small"
              sx={{
                transition: 'transform 200ms',
                transform: expanded ? 'rotate(180deg)' : 'none',
              }}
            />
          </IconButton>
        )}
      </Box>
      {suspiciousBotClick && (
        <Typography
          sx={{
            contain: 'inline-size',
            backgroundColor: '#FFFBEB',
            borderTop: '1px solid #FDE68A',
            p: '8px 14px',
            fontSize: 12,
            lineHeight: 1.45,
            color: '#92400E',
          }}
        >
          {`This IP also clicked ${otherClicks} other link${
            otherClicks > 1 ? 's' : ''
          }, so it may be a bot.`}
        </Typography>
      )}
      {hasDetails && (
        <Collapse in={expanded} unmountOnExit>
          <Stack
            spacing="10px"
            sx={{
              contain: 'inline-size',
              borderTop: '1px solid #E5E7EB',
              p: '12px 14px',
              fontSize: 12,
              lineHeight: 1.45,
              wordBreak: 'break-all',
            }}
          >
            <LinkDetailRow label="Destination" value={url} />
            <LinkDetailRow label="Short URL" value={shortUrl} />
            <LinkDetailRow label="IP" value={ip} />
          </Stack>
        </Collapse>
      )}
    </Box>
  );
};

const SystemMediaMessage: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography component="span" fontStyle="italic">
    {children}
  </Typography>
);

export const Media: FC<{ media: Conversation_externalLead_conversation_inboundSms_media }> = ({
  media,
}) => {
  if (!media.url) {
    return (
      <Box>
        <SystemMediaMessage>
          Media with type &quot;{media.contentType}&quot; is not available
        </SystemMediaMessage>
      </Box>
    );
  }

  if (media.contentType?.startsWith('image')) {
    return (
      <Box component="img" src={media.url} borderRadius="5px" minWidth="300px" maxWidth="100%" />
    );
  }
  if (media.contentType?.startsWith('audio')) {
    return (
      <audio controls>
        <source src={media.url} type={media.contentType} style={{ width: '100%', minWidth: 300 }} />
      </audio>
    );
  }
  if (media.contentType?.startsWith('video')) {
    return (
      <video controls>
        <source src={media.url} type={media.contentType} style={{ width: '100%', minWidth: 300 }} />
      </video>
    );
  }

  return (
    <Box>
      <SystemMediaMessage>
        File with type &quot;{media.contentType}&quot; cannot be previewed
      </SystemMediaMessage>
      <TooltipIconButtonLink link={media.url} tooltip="Open in new tab">
        <icons.LaunchRounded />
      </TooltipIconButtonLink>
    </Box>
  );
};

/**
 * Applied only to bubbles that render a `menu`, so the nine other ChatBubble
 * callers (calls, recordings, lead events, RCS) are untouched: anchors the
 * absolutely-positioned button, reserves room so the text can't run under it,
 * and reveals it on hover — or while its menu is open.
 */
const inboundMenuBubbleSx = {
  position: 'relative',
  paddingRight: '35px',
  [`& .${DATADOG_MENU_BUTTON_CLASS}`]: { opacity: 0, transition: 'opacity 0.2s' },
  [`&:hover .${DATADOG_MENU_BUTTON_CLASS}, & .${DATADOG_MENU_BUTTON_CLASS}[aria-expanded='true']`]:
    { opacity: 1 },
};

export const InboundSmsAndMedia: FC<{
  inboundSms: Conversation_externalLead_conversation_inboundSms;
  /** Optional hover-revealed menu; the bubble only reserves space when given one. */
  menu?: ReactNode;
}> = ({ inboundSms, menu }) => {
  return (
    <ChatBubble sx={menu ? inboundMenuBubbleSx : undefined}>
      {menu}
      <Stack gap={1}>
        {inboundSms.media?.map((m) => (
          <Media key={m.url} media={m} />
        ))}
        {inboundSms.body}
      </Stack>
    </ChatBubble>
  );
};

export const ScheduledPreviewBubble: FC<{
  item: Conversation_externalLead_scheduledOutreach;
  tz: string;
}> = ({ item, tz }) => {
  const when = moment(item.when).tz(tz);
  const previewRaw = item.messagePreview?.trim();
  const preview = previewRaw?.replace(/\\n/g, '\n');

  const header = (
    <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
      <Box component="span" sx={{ verticalAlign: 'middle', display: 'inline-flex', mr: 1 }}>
        {iconForItem(item)}
      </Box>
      {labelForItem(item)} {when.fromNow()}
    </Typography>
  );

  return (
    <ChatMessage className="right">
      <Box display="flex" alignItems="center">
        <ChatBubble
          className="right"
          sx={{
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            maxWidth: 680,
            paddingRight: 2,
          }}
        >
          {header}
          {preview && <Typography sx={{ whiteSpace: 'pre-wrap' }}>{preview}</Typography>}
        </ChatBubble>
        <AktifyIcon2 />
      </Box>
      <Typography variant="body2" color="textSecondary" paddingX="38px">
        <Timestamp>{when.format('MM/D/YYYY h:mm A')}</Timestamp>
      </Typography>
    </ChatMessage>
  );
};
